import { GuidanceAnyItem } from './StructuredGuidance';

export type CaseTypeGuidance = {
  items: GuidanceAnyItem[];
};

export const course1CaseTypeContent: Record<'Simple' | 'Complex' | 'Psych', CaseTypeGuidance> = {
  Simple: {
    items: [
      { kind: 'title', content: 'What Constitutes a Simple Case?' },
      {
        kind: 'description',
        content: (
          <>If the primary ailment is physical in nature, and <strong>4 or more</strong> of these factors are present, then the case is likely simple:</>
        )
      },
      {
        kind: 'bullets',
        items: [
          'Acute condition (likely to last less than 3 months)',
          'Primary symptoms only, no complications',
          'Mild to moderate symptom rating (Less than 6 on a 1-10 scale)',
          'Not likely to result in hospitalization, surgery, or death (EX: frozen shoulder)',
          'Involves working on less than 5 chakras (EX: headache)',
          'Likely to respond to medication or rest and can only be managed (EX: simple sprain)',
          'Likely to have one cause (EX: common cold)'
        ]
      },
      { kind: 'divider' },
      { kind: 'title', content: 'What’s Considered a Successful Simple Case?' },
      {
        kind: 'bullets',
        items: [
          '60% or more reduction in symptoms - either in frequency, duration, or intensity',
          'Sustained improvement NOT required',
          'No minimum number of sessions required'
        ]
      }
    ]
  },
  Complex: {
    items: [
      { kind: 'title', content: 'What Constitutes a Complex Case?' },
      {
        kind: 'description',
        content: (
          <>If the primary ailment is physical in nature, and <strong>4 or more</strong> of these factors are present, then the case is likely complex:</>
        )
      },
      {
        kind: 'bullets',
        items: [
          'Chronic condition (likely to last 3 months or longer)',
          'Primary symptoms + complications present',
          'Moderate to severe symptom rating (6 or higher on a 1-10 scale)',
          'Likely to result in hospitalization, surgery, or death (EX: appendicitis)',
          'Involves working on more than 5 chakras (EX: emphysema)',
          'Not likely to respond to medication or rest and can only be managed (EX: Osteoarthritis)',
          'Likely to have multiple factors causing & contributing to the problem (EX: cancer)'
        ]
      },
      {
        kind: 'description',
        content: (
          <><strong>Note:</strong> If it’s a physical case and less than 4 of the above factors are present, it probably should be categorized as a simple case.</>
        )
      },
      { kind: 'divider' },
      { kind: 'title', content: 'What’s Considered a Successful Complex Case?' },
      { kind: 'description', content: <strong>Case has less than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '50% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: <strong>Case has more than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '30% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: 'OR' },
      {
        kind: 'bullets',
        items: [
          '30% or more reduction in any of the symptoms related to the ailment with each session',
          'Sustained improvement is not required, just 30% or more improvement with each session'
        ]
      }
    ]
  },
  Psych: {
    items: [
      { kind: 'title', content: 'What Constitutes a Psych Case?' },
      {
        kind: 'description',
        content: (
          <>If the primary ailment is emotional or psychological in nature, and a Pranic Psychotherapy protocol was applied, then it’s likely a psych case.</>
        )
      },
      { kind: 'divider' },
      { kind: 'title', content: 'What’s Considered a Successful Psych Case?' },
      { kind: 'description', content: <strong>Case has less than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '50% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: <strong>Case has more than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '30% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: 'OR' },
      {
        kind: 'bullets',
        items: [
          '30% or more reduction in any of the symptoms related to the ailment with each session',
          'Sustained improvement is not required, just 30% or more improvement with each session'
        ]
      }
    ]
  }
};

export const course2CaseTypeContent: Record<'Complex' | 'Psych', CaseTypeGuidance> = {
  Complex: {
    items: [
      { kind: 'title', content: 'What Constitutes a Complex Case?' },
      {
        kind: 'description',
        content: (
          <>If the primary ailment is physical in nature, and <strong>4 or more</strong> of these factors are present, then the case is likely complex:</>
        )
      },
      {
        kind: 'bullets',
        items: [
          'Chronic condition (likely to last 3 months or longer)',
          'Primary symptoms + complications present',
          'Moderate to severe symptom rating (6 or higher on a 1-10 scale)',
          'Likely to result in hospitalization, surgery, or death (EX: appendicitis)',
          'Involves working on more than 5 chakras (EX: emphysema)',
          'Not likely to respond to medication or rest and can only be managed (EX: Osteoarthritis)',
          'Likely to have multiple factors causing & contributing to the problem (EX: cancer)'
        ]
      },
      {
        kind: 'description',
        content: (
          <><strong>Note:</strong> If it’s a physical case and less than 4 of the above factors are present, it probably should be categorized as a simple case.</>
        )
      },
      { kind: 'divider' },
      { kind: 'title', content: 'What’s Considered a Successful Complex Case?' },
      { kind: 'description', content: <strong>Case has less than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '60% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: <strong>Case has more than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '40% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: 'OR' },
      {
        kind: 'bullets',
        items: [
          '40% or more reduction in any of the symptoms related to the ailment with each session',
          'Sustained improvement is not required, just 40% or more improvement with each session'
        ]
      }
    ]
  },
  Psych: {
    items: [
      { kind: 'title', content: 'What Constitutes a Psych Case?' },
      {
        kind: 'description',
        content: (
          <>If the primary ailment is emotional or psychological in nature, and a Pranic Psychotherapy protocol was applied, then it’s likely a psych case.</>
        )
      },
      { kind: 'divider' },
      { kind: 'title', content: 'What’s Considered a Successful Psych Case?' },
      { kind: 'description', content: <strong>Case has less than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '60% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: <strong>Case has more than 4 Sessions</strong> },
      {
        kind: 'bullets',
        items: [
          '40% or more reduction in symptoms or quality of life improvements',
          'Sustained improvement of 1 week or more is required'
        ]
      },
      { kind: 'description', content: 'OR' },
      {
        kind: 'bullets',
        items: [
          '40% or more reduction in any of the symptoms related to the ailment with each session',
          'Sustained improvement is not required, just 40% or more improvement with each session'
        ]
      }
    ]
  }
};

export const getCaseTypeContent = (courseId?: number): Record<'Simple' | 'Complex' | 'Psych', CaseTypeGuidance> => {
  if (courseId === 1) {
    return course1CaseTypeContent;
  }
  if (courseId === 2) {
    return {
      Simple: course1CaseTypeContent.Simple,
      Complex: course2CaseTypeContent.Complex,
      Psych: course2CaseTypeContent.Psych,
    };
  }
  return {
    Simple: { items: [] },
    Complex: { items: [] },
    Psych: { items: [] },
  };
};

export const validateGuidanceItems = (items: GuidanceAnyItem[]): boolean => {
  const validKinds = new Set(['title', 'description', 'bullets', 'divider']);
  for (const item of items) {
    if (!item || !validKinds.has(item.kind)) return false;
    if (item.kind === 'bullets' && !Array.isArray(item.items)) return false;
    if ((item.kind === 'title' || item.kind === 'description') && item.content === undefined) return false;
  }
  return true;
};
